import React, { useState, useCallback } from 'react';
import { Button, Typography } from 'react-covideo-common';
import { useTheme } from 'styled-components/macro';
import {
  FileDropZoneContainer,
  HiddenInput,
  FileInfoContainer,
  PreviewImage,
  PreviewAudio,
} from './styles';
import { errorToast } from 'lib/components/toasts/error';
import {
  AcceptedFileExtension,
  FileInfo,
  FileLoadedData,
  fileTypeMapper,
  StyleObject,
} from './constants';
import { getAcceptDescription } from './helpers';
import { MdDeleteForever } from 'react-icons/md';
import { Flex } from 'lib/components/styles/layout';
import { Trans, useTranslation } from 'react-i18next';

interface FileDropZoneProps {
  accept?: AcceptedFileExtension[];
  extendStyles?: StyleObject;
  onFileLoaded?: (data: FileLoadedData, fileInfo: FileInfo, file: File) => void;
  onFileRemoved?: () => void;
  encoding?: string;
  disabled?: boolean;
}

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  accept = ['image/*', 'audio/*'],
  extendStyles,
  onFileLoaded,
  onFileRemoved,
  encoding = 'UTF-8',
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);

  const processFile = useCallback(
    (file: File) => {
      if (file.type.startsWith('image/') && file.size > MAX_IMAGE_SIZE_BYTES) {
        errorToast({
          title:
            'The selected image exceeds the 10 MB limit. Please choose a smaller file.',
        });
        return;
      }

      const info: FileInfo = {
        name: file.name,
        size: file.size,
        type: file.type,
      };

      // Validate extension and type
      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      const isAllowed = accept.some(acc =>
        acc.startsWith('.')
          ? acc === extension
          : file.type.match(acc.replace('*', '.*'))
      );
      if (!isAllowed) {
        errorToast({ title: `File type "${extension}" is not supported.` });
        return;
      }

      // Prepare file info
      const reader = new FileReader();
      setFileInfo(info);

      // Clean up old object URLs to prevent leaks
      if (preview) URL.revokeObjectURL(preview);

      // Pick handler key dynamically
      let handlerKey: keyof typeof fileTypeMapper = 'default';
      if (file.name.toLowerCase().endsWith('.csv')) handlerKey = 'csv';
      else if (file.type.startsWith('image/')) handlerKey = 'image';
      else if (file.type.startsWith('audio/')) handlerKey = 'audio';

      // Run handler
      fileTypeMapper[handlerKey](
        file,
        info,
        reader,
        (data: FileLoadedData, fileInfo: FileInfo) => {
          onFileLoaded?.(data, fileInfo, file);
        },
        encoding,
        setPreview
      );
    },
    [onFileLoaded, encoding, accept, preview]
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) processFile(files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveFile = useCallback(() => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setFileInfo(null);
    // Reset the hidden file input so same file can be re-selected
    const input = document.getElementById(
      'hiddenFileInput'
    ) as HTMLInputElement;
    if (input) input.value = '';
    onFileRemoved?.();
  }, [preview, onFileRemoved]);

  const dropMessage = dragActive ? (
    t('Drop your file here...')
  ) : (
    <Trans
      i18nKey='dragAndDropOrBrowse'
      values={{
        accept: getAcceptDescription(accept),
      }}
    />
  );

  return (
    <FileDropZoneContainer
      dragActive={dragActive}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => document.getElementById('hiddenFileInput')?.click()}
      disabled={disabled}
      style={extendStyles?.container}
    >
      <HiddenInput
        id='hiddenFileInput'
        type='file'
        accept={accept.join(',')}
        onChange={handleFileInput}
        disabled={disabled}
      />

      <Typography color={theme.colors.neutral[80]}>{dropMessage}</Typography>

      {fileInfo && (
        <FileInfoContainer>
          <Flex flexDirection='row' gap='8px' alignItems='center'>
            <strong>{fileInfo.name}</strong>
            <Typography
              variant='small'
              fontWeight={400}
              color={theme.colors.neutral[80]}
            >
              ({(fileInfo.size / 1024).toFixed(1)} KB)
            </Typography>
            <Button
              icon={
                <MdDeleteForever size={18} color={theme.colors.danger[100]} />
              }
              extendStyles={{ container: { padding: 0, minWidth: 0 } }}
              onClick={e => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              variant='transparent'
            />
          </Flex>
        </FileInfoContainer>
      )}

      {preview && fileInfo?.type.startsWith('image/') && (
        <PreviewImage src={preview} alt='Preview' />
      )}

      {preview && fileInfo?.type.startsWith('audio/') && (
        <PreviewAudio key={preview} controls>
          <source src={preview} type={fileInfo.type} />
        </PreviewAudio>
      )}
    </FileDropZoneContainer>
  );
};
